import { Link } from 'react-router-dom';
import { Mail, Heart, Facebook, Instagram, Linkedin, Youtube, Twitter } from 'lucide-react';
import { NewsletterForm } from '../forms/NewsletterForm';

const footerLinks = {
  branches: [
    { name: 'AMS Events', href: '/events' },
    { name: 'AMS University', href: '/university' },
    { name: 'AMS Compass', href: '/compass' },
  ],
  company: [
    { name: 'Vendors', href: '/vendors' },
    { name: 'Gallery', href: '/gallery' },
    { name: 'Contact Us', href: '/contact' },
  ],
  legal: [
    { name: 'Terms & Conditions', href: '/terms' },
    { name: 'Privacy Policy', href: '/privacy' },
  ],
};

export function Footer() {
  return (
    <footer className="bg-primary text-primary-foreground" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
            {/* Brand Column */}
            <div className="lg:col-span-2">
              <div className="mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_craft-collective-11/artifacts/cpg0ir3f_AMS%20Logo.jpg" 
                  alt="Artisan Market & Studio" 
                  className="h-20 w-auto object-contain bg-white rounded-lg p-2"
                />
              </div>
              <p className="text-white/80 text-lg leading-relaxed mb-8 max-w-md">
                Empowering artisans to turn passion into profit. One Vision. Three Powerful Branches.
              </p>
              {/* Newsletter in Footer */}
              <div className="max-w-sm">
                <h4 className="font-serif text-lg font-semibold mb-4">Stay Connected</h4>
                <NewsletterForm variant="footer" />
              </div>
            </div>

            {/* Links Columns */}
            <div>
              <h4 className="font-serif text-lg font-semibold mb-6">Our Branches</h4>
              <ul className="space-y-4">
                {footerLinks.branches.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="text-white/70 hover:text-white transition-colors"
                      data-testid={`footer-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-serif text-lg font-semibold mb-6">Quick Links</h4>
              <ul className="space-y-4">
                {footerLinks.company.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="text-white/70 hover:text-white transition-colors"
                      data-testid={`footer-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
              
              {/* Contact Info */}
              <div className="mt-8 space-y-3">
                <a href="mailto:info@artisanmarketandstudio.com" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors">
                  <Mail className="w-4 h-4" />
                  <span className="text-sm">info@artisanmarketandstudio.com</span>
                </a>
                <div className="flex items-center gap-2 text-white/70">
                  <Heart className="w-4 h-4" />
                  <span className="text-sm">Proudly serving the DMV area since 2024</span>
                </div>
              </div>

              {/* Legal Links */}
              <div className="mt-6 space-y-2">
                {footerLinks.legal.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="block text-white/50 hover:text-white/70 transition-colors text-sm"
                    data-testid={`footer-${link.name.toLowerCase().replace(/\s/g, '-').replace('&', 'and')}`}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <p className="text-white/60 text-sm">
                © 2026 The Motley Maker LLC. All Rights Reserved.
              </p>
              <p className="text-white/40 text-xs mt-1">
                Artisan Market &amp; Studio is a trademark of The Motley Maker LLC.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white transition-colors" data-testid="footer-facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white transition-colors" data-testid="footer-instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white transition-colors" data-testid="footer-linkedin">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white transition-colors" data-testid="footer-youtube">
                <Youtube className="w-5 h-5" />
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white transition-colors" data-testid="footer-twitter">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="https://pinterest.com" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white transition-colors" data-testid="footer-pinterest">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
